import React, { useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import FormInput from '../../components/ui/FormInput';
import Card, { CardContent } from '../../components/ui/Card';
import { twoFactorService } from '../../services/twoFactorService';

const Settings2FA: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [loading, setLoading] = useState(false);

  const loadStatus = async () => {
    const res = await twoFactorService.getStatus();
    setEnabled(res.enabled);
    setBackupCodesRemaining(res.backupCodesRemaining);
  };

  useEffect(() => { loadStatus(); }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await twoFactorService.setup();
      setQrCode(res.qrCode);
      setSetupMode(true);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!token || token.length !== 6) {
      alert('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await twoFactorService.enable(token);
      setBackupCodes(res.backupCodes);
      setEnabled(true);
      setSetupMode(false);
      setToken('');
      alert('2FA enabled! Please save your backup codes.');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Invalid token');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disableToken || disableToken.length !== 6) {
      alert('Please enter a 6-digit code or backup code');
      return;
    }
    setLoading(true);
    try {
      await twoFactorService.disable(disableToken);
      setEnabled(false);
      setDisableToken('');
      await loadStatus();
      alert('2FA disabled successfully');
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Invalid token');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Two-Factor Authentication</h1>

      {!enabled ? (
        <Card>
          <CardContent className="p-6">
            {!setupMode ? (
              <div className="space-y-4">
                <p>Add an extra layer of security to your account with two-factor authentication.</p>
                <Button onClick={handleSetup} loading={loading}>Enable 2FA</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
                {qrCode && <img src={qrCode} alt="QR Code" className="mx-auto w-64 h-64" />}
                <FormInput
                  label="Enter 6-digit code"
                  value={token}
                  onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                />
                <div className="flex gap-2">
                  <Button onClick={handleEnable} loading={loading}>Verify & Enable</Button>
                  <Button variant="outline" onClick={() => { setSetupMode(false); setQrCode(null); }}>Cancel</Button>
                </div>
              </div>
            )}
            {backupCodes.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded">
                <p className="font-medium mb-2">Save these backup codes!</p>
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="font-mono text-sm">{code}</div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <p className="text-green-600 font-medium">✓ 2FA is enabled</p>
              <p className="text-sm text-gray-600">Backup codes remaining: {backupCodesRemaining}</p>
              <FormInput
                label="Enter code to disable"
                value={disableToken}
                onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="6-digit code or backup code"
              />
              <Button variant="danger" onClick={handleDisable} loading={loading}>Disable 2FA</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Settings2FA;


